/**
 * Shared axios auth/tenant/document-provider interceptors.
 *
 * Both API layers — the orval mutator (`client.ts`) and the
 * swagger-typescript-api `Api` instance (`apiInstance.ts`) — used to carry
 * near-identical copies of this logic. They are consolidated here so the auth
 * fix (renew-or-fail tokens + routing 401s to the session-expired modal) can't
 * be half-applied to one layer and not the other.
 *
 * The NetDocuments-specific 401 refresh path is preserved for both: a request
 * that carried an `nd_access_token` and got a 401 refreshes the ND token and
 * replays once, WITHOUT touching the main app session.
 */
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authConfig } from '@/config/auth.config';
import { resolveTenant } from '@/tenant/tenantResolver';
import {
  getValidNdAccessToken,
  getStoredNdToken,
  refreshNdToken,
} from '@/utils/ndTokenManager';
import { handleUnauthorizedResponse } from '@/auth/sessionExpiry';

/** ABP tenant header name */
const TENANT_HEADER = '__tenant';
const LANGUAGE_STORAGE_KEY = 'motr_language'; // kept in sync with i18n/localization.service

const getStoredLanguage = (): string | null => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) || navigator.language || null;
  } catch {
    return null;
  }
};

/**
 * Default token getter – reads directly from the OIDC localStorage store so
 * API calls that fire before AppShell installs the real getter still carry a
 * Bearer token.
 */
export const getOidcTokenFromStorage = (): string | null => {
  try {
    const storageKey = `oidc.user:${authConfig.authority}:${authConfig.client_id}`;
    const userJson = localStorage.getItem(storageKey);
    if (userJson) {
      const user = JSON.parse(userJson);
      if (user?.access_token && user.expires_at > Math.floor(Date.now() / 1000)) {
        return user.access_token;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
};

const getImanageAuthToken = (): string =>
  localStorage.getItem('Imanage_access_token') || '';

const getGoogleDriveAuthToken = (): string =>
  localStorage.getItem('GoogleDriveToken') || '';

/**
 * Check if URL needs document provider headers.
 * Matches Angular's http-interceptor.ts logic.
 */
const needsDocumentProviderHeaders = (url: string): boolean =>
  url.includes('app/documents-provider') ||
  // The messages controller is routed at /api/app/messages-extn (plural); keep the
  // singular too so no older caller loses its header.
  url.includes('app/messages-extn') ||
  url.includes('app/message-extn') ||
  url.includes('ViewDocument') ||
  url.includes('UploadDocument') ||
  url.includes('app/ai-extn') ||
  url.includes('app/document-templates') ||
  url.includes('NDApi/') ||
  // Job creation copies each template document into the job's own repository location,
  // which must happen with the creating user's own repository token.
  /app\/jobs-ext\/add(\?|$)/.test(url);

export interface AuthInterceptorOptions {
  /**
   * Returns the access token to attach. Each API layer keeps its own mutable
   * getter (swapped in by AppShell via setTokenGetter / setApiTokenGetter), so
   * the helper reads it lazily on every request.
   */
  getToken: () => Promise<string | null>;
}

/**
 * Attach the shared request + response interceptors to an axios instance.
 */
export const attachAuthInterceptors = (
  instance: AxiosInstance,
  { getToken }: AuthInterceptorOptions
): void => {
  // ── Request: auth token, tenant header, document-provider headers ────────
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Tell the API which culture to localize server-built text in
      // (action names, group labels, validation messages). ABP's request
      // localization reads Accept-Language, so without this every
      // server-localized string comes back in the default (English).
      if (!config.headers['Accept-Language']) {
        const lang = getStoredLanguage();
        if (lang) {
          config.headers['Accept-Language'] = lang;
        }
      }

      // Add tenant header if not already set
      if (!config.headers[TENANT_HEADER]) {
        const tenantName = resolveTenant();
        if (tenantName) {
          config.headers[TENANT_HEADER] = tenantName;
        }
      }

      // Add document provider headers (NetDocuments, iManage, Google Drive)
      if (config.url && needsDocumentProviderHeaders(config.url)) {
        // Expiry-aware getter – refreshes the ND token automatically if expired
        const ndToken = await getValidNdAccessToken();
        if (ndToken) {
          config.headers['nd_access_token'] = ndToken;
        }
        config.headers['override-using-service-account'] =
          localStorage.getItem('override-service-account') || 'false';
        config.headers['Imanage_access_token'] = getImanageAuthToken();
        config.headers['google_drive_token'] = getGoogleDriveAuthToken();
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // ── Response: ND 401 refresh path, then session-expiry handling ──────────
  instance.interceptors.response.use(
    (response) => {
      const isImp = sessionStorage.getItem('motr_impersonating') === 'true';
      if (isImp) {
        const data = response.data;
        const isEmpty =
          data === null ||
          data === undefined ||
          (Array.isArray(data) && data.length === 0) ||
          (typeof data === 'object' &&
            data?.items &&
            data.items.length === 0 &&
            data.totalCount === 0);
        if (isEmpty) {
          console.warn('[Impersonation:Client] Empty response during impersonation', {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            hasAuthHeader: !!response.config.headers?.Authorization,
            data: JSON.stringify(data).substring(0, 200),
          });
        }
      }
      return response;
    },
    async (error: AxiosError) => {
      const isImp = sessionStorage.getItem('motr_impersonating') === 'true';
      if (isImp) {
        console.error('[Impersonation:Client] Error during impersonation', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          hasAuthHeader: !!error.config?.headers?.Authorization,
          responseData: JSON.stringify(error.response?.data).substring(0, 300),
        });
      }

      // ND-specific 401 retry: if the request carried an nd_access_token and
      // got a 401, refresh the ND token and replay the request once. This must
      // NOT touch the main app session — ND auth is handled separately.
      const cfg = error.config as
        | (InternalAxiosRequestConfig & { _ndRetried?: boolean })
        | undefined;
      if (
        error.response?.status === 401 &&
        cfg &&
        !cfg._ndRetried &&
        cfg.headers?.['nd_access_token'] &&
        cfg.url &&
        needsDocumentProviderHeaders(cfg.url)
      ) {
        const stored = getStoredNdToken();
        if (stored?.refresh_token) {
          const ok = await refreshNdToken(stored.refresh_token);
          if (ok) {
            cfg._ndRetried = true;
            const freshToken = await getValidNdAccessToken();
            if (freshToken) {
              cfg.headers['nd_access_token'] = freshToken;
            }
            return instance(cfg);
          }
        }
        // ND refresh was attempted – don't drive the main-app session-expired
        // flow. ND auth state is surfaced via the nd-auth-changed event.
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        // Expired / rejected main-app token — route to the session-expired
        // flow (attempts a silent renew, else shows the global modal).
        void handleUnauthorizedResponse();
      }
      if (error.response?.status === 403) {
        // Forbidden - user lacks permission
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
      }
      return Promise.reject(error);
    }
  );
};
